import type {
  PersistedClient,
  Persister,
} from '@tanstack/query-persist-client-core'

import type { CacheGeneration } from './cache-scope'
import { cacheKeyOwner, cacheUserPrefixes } from './cache-scope'

type SqliteDB = Awaited<
  ReturnType<typeof import('@tauri-apps/plugin-sql').default.load>
>

let dbPromise: Promise<SqliteDB> | null = null

async function getDb(): Promise<SqliteDB> {
  if (!dbPromise) {
    dbPromise = (async () => {
      const mod = await import('@tauri-apps/plugin-sql')
      const db = await mod.default.load('sqlite:cache.db')
      await db.execute(`CREATE TABLE IF NOT EXISTS query_cache (
        key TEXT PRIMARY KEY,
        value TEXT NOT NULL,
        updated_at INTEGER NOT NULL
      )`)
      await db.execute(
        'CREATE TABLE IF NOT EXISTS deleted_cache_owners (user_id TEXT PRIMARY KEY)',
      )
      await db.execute(
        'CREATE TABLE IF NOT EXISTS cache_owner_generations (user_id TEXT PRIMARY KEY, generation TEXT NOT NULL)',
      )
      await db.execute(
        'CREATE TABLE IF NOT EXISTS cache_forget_operations (user_id TEXT NOT NULL, operation_id TEXT NOT NULL, completed INTEGER NOT NULL DEFAULT 0, PRIMARY KEY (user_id, operation_id))',
      )
      return db
    })().catch(error => {
      dbPromise = null
      throw error
    })
  }
  return dbPromise
}

export async function removeSQLiteCaches(
  prefix: string,
  expected?: CacheGeneration,
): Promise<void> {
  const db = await getDb()
  await db.execute(
    'DELETE FROM query_cache WHERE substr(key, 1, length($1)) = $1 AND ($2 IS NULL OR (SELECT generation FROM cache_owner_generations WHERE user_id = $2) IS $3)',
    [prefix, expected?.ownerId ?? null, expected?.generation ?? null],
  )
}

export async function deleteSQLiteUserCaches(userId: string): Promise<void> {
  const db = await getDb()
  await db.execute(
    'INSERT OR IGNORE INTO deleted_cache_owners (user_id) VALUES ($1)',
    [userId],
  )
  for (const prefix of cacheUserPrefixes(userId))
    await removeSQLiteCaches(prefix)
}

/** Every phase is conditional on the same unfinished operation. This remains
 * safe across pooled SQL connections and crashes between statements. */
export async function forgetSQLiteUserCaches(
  userId: string,
  operationId: string,
): Promise<void> {
  if (!userId || !operationId || operationId.length > 128)
    throw new Error('Cache removal requires an owner and operation')
  const db = await getDb()
  await db.execute(
    'INSERT OR IGNORE INTO cache_forget_operations (user_id, operation_id) VALUES ($1, $2)',
    [userId, operationId],
  )
  await db.execute(
    `INSERT INTO cache_owner_generations (user_id, generation)
    SELECT $1, $2 WHERE EXISTS (SELECT 1 FROM cache_forget_operations WHERE user_id = $1 AND operation_id = $2 AND completed = 0)
    ON CONFLICT(user_id) DO UPDATE SET generation = excluded.generation`,
    [userId, operationId],
  )
  for (const prefix of cacheUserPrefixes(userId))
    await db.execute(
      'DELETE FROM query_cache WHERE substr(key, 1, length($1)) = $1 AND EXISTS (SELECT 1 FROM cache_forget_operations WHERE user_id = $2 AND operation_id = $3 AND completed = 0)',
      [prefix, userId, operationId],
    )
  await db.execute(
    'UPDATE cache_forget_operations SET completed = 1 WHERE user_id = $1 AND operation_id = $2',
    [userId, operationId],
  )
}

export async function createSQLitePersister(
  cacheKey: string,
  scopePrefix = cacheKey,
): Promise<Persister> {
  const db = await getDb()
  const owner = cacheKeyOwner(cacheKey)
  const generation = owner
    ? ((
        await db.select<{ generation: string }[]>(
          'SELECT generation FROM cache_owner_generations WHERE user_id = $1',
          [owner],
        )
      )[0]?.generation ?? null)
    : null
  return {
    persistClient: async (client: PersistedClient) => {
      try {
        const value = JSON.stringify(client)
        await db.execute(
          `INSERT OR REPLACE INTO query_cache (key, value, updated_at) SELECT $1, $2, $3 WHERE NOT EXISTS (SELECT 1 FROM deleted_cache_owners WHERE user_id = $4)
           AND NOT EXISTS (SELECT 1 FROM cache_forget_operations WHERE user_id = $4 AND completed = 0)
           AND (SELECT generation FROM cache_owner_generations WHERE user_id = $4) IS $5`,
          [cacheKey, value, Date.now(), owner, generation],
        )
      } catch {
        console.warn('[QueryCache:SQLite] Failed to persist client')
      }
    },
    restoreClient: async () => {
      try {
        const rows = await db.select<{ value: string }[]>(
          `SELECT value FROM query_cache WHERE (key = $1 OR (substr(key, 1, length($2)) = $2 AND substr(key, length($2) + 1, 7) != 'schema-'))
           AND NOT EXISTS (SELECT 1 FROM deleted_cache_owners WHERE user_id = $3)
           AND NOT EXISTS (SELECT 1 FROM cache_forget_operations WHERE user_id = $3 AND completed = 0)
           AND (SELECT generation FROM cache_owner_generations WHERE user_id = $3) IS $4
           ORDER BY CASE WHEN key = $1 THEN 0 ELSE 1 END, updated_at DESC LIMIT 1`,
          [cacheKey, scopePrefix, owner, generation],
        )
        if (rows.length === 0) return undefined
        return JSON.parse(rows[0].value) as PersistedClient
      } catch {
        console.warn('[QueryCache:SQLite] Failed to restore client')
        return undefined
      }
    },
    removeClient: async () => {
      try {
        await removeSQLiteCaches(
          scopePrefix,
          owner ? { ownerId: owner, generation } : undefined,
        )
      } catch {
        console.warn('[QueryCache:SQLite] Failed to remove client')
      }
    },
  }
}
